import React, { useState, useEffect, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    IconButton,
    Typography,
    Tooltip,
    Grid,
} from '@mui/material';
import i18next from 'i18next';
import { useMap, MapContainer, TileLayer, Marker, Popup, ZoomControl } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L, { LatLngExpression } from 'leaflet';
import makeStyles from '@mui/styles/makeStyles';
import { API_KEY } from '@/config/constants';
import { localTime, readableDuration, parseTimeStringToDate } from '../../Common/timeUtils';
import {
    deliveryMarkerMaker,
    pickupMarkerMaker
} from '../../Common/textMarkerMaker';
import {
    AccessTimeOutlined,
    ArrowBackIosOutlined,
    ArrowForwardIosOutlined,
    AssignmentIndOutlined,
    CloseOutlined,
    ConfirmationNumberOutlined,
    CorporateFareOutlined,
    DangerousOutlined,
    DepartureBoardOutlined,
    GarageOutlined,
    InfoOutlined,
    LocationCityOutlined,
    LowPriorityOutlined,
    ScaleOutlined,
    TimelapseOutlined
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { useSettings } from '@/components/Settings/SettingsContext';
import { RootState } from '@/index';
import { ShipmentPoints } from './ADR';

interface DetailShipmentFormProps {
    open: boolean;
    onClose: () => void;
    stop: any;
    shipments?: any[];
}

interface Coordinates {
    latitude: number;
    longitude: number;
}

interface ChangeMapViewProps {
    coordinates: Coordinates | null;
}

interface TimeWindow {
    start: string | null;
    end: string | null;
}

const useStyles = makeStyles({
    Dialogstyle: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    Dialogtitle: {
        background: 'rgba(239, 89, 39, 0.12)',
        display: 'flex',
        alignItem: 'center'
    },
    closeIconBtn: {
        position: 'absolute',
        right: 12,
        top: 8,
        padding: 4
    },
    Textlabel: {
        color: 'grey'
    },
    container: {
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
        padding: 10,
        margin: '20px 0 20px 0'
    },
    infoRow: {
        display: 'flex',
        justifyContent: 'space-between',
        gap: 20
    },
    infoColumn: {
        flex: 1,
    },
    mapContainer: {
        flex: 1,
        margin: 5
    },
    locationPop: {
        bottom: '28px !important'
    },
    largeGap: {
        boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
        display: 'flex',
        alignItems: 'center',
        padding: 10
    },
    leftInfoItem: {
        width: '30%'
    },
    labelText: {
        fontWeight: 600,
        color: '#000',
        marginBottom: 10,
        fontSize: 15
    },
    addresslabel: {
        fontWeight: 600,
        color: '#000',
        marginBottom: 5,
        fontSize: 15
    },
    Dialogcontent: {
        paddingTop: '16px'
    },
    OrderSepration: {
        background: 'rgba(239, 89, 39, 0.12)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '10px',
        padding: '10px'
    }
});

const DetailShipmentForm: React.FC<DetailShipmentFormProps> = ({
    open,
    onClose,
    stop,
    shipments
}) => {
    const classes = useStyles();
    const coordinates = stop?.location?.coordinates ?? null;
    const address = stop?.location?.address ?? null;
    const safeShipments = useMemo(
        () => (Array.isArray(shipments) ? shipments : []),
        [shipments]
    );
    const total = safeShipments.length;
    const [currentIdx, setCurrentIdx] = useState(0);
    const handlePrev = () => setCurrentIdx(i => Math.max(0, i - 1));
    const handleNext = () => setCurrentIdx(i => Math.min(total - 1, i + 1));
    // constant bahar bana lo taa ki stable rahe
    const EMPTY_TW: TimeWindow = { start: null, end: null };

    const { settings } = useSettings();
    const dimensions = settings.load_dimensions || [];
    const shipmentSettings = useSelector(
        (s: RootState) => s.core.shipmentSettings
    );

    const pickup_time_window: TimeWindow =
        shipmentSettings?.pickup_time_window ?? EMPTY_TW;

    const delivery_time_window: TimeWindow =
        shipmentSettings?.delivery_time_window ?? EMPTY_TW;

    // helpers to map label → backend loads index
    const normalize = (s?: string) =>
        (s || '')
            .toLowerCase()
            .normalize('NFKD')
            .replace(/[\s._\-[\]()]/g, '');

    const getLoadIndexForLabel = (label: string) => {
        const n = normalize(label);
        if (n.includes('adr')) return 2; 
        if (n.includes('stell') || n.includes('slot') || n.includes('palette') || n.includes('palett'))
            return 1;
        return 0;
    };

    const pickIconForLabel = (label: string) => {
        const n = normalize(label);
        if (n.includes('adr')) return DangerousOutlined;
        if (n.includes('stell') || n.includes('slot') || n.includes('palette') || n.includes('palett'))
            return GarageOutlined;
        return ScaleOutlined;
    };

    useEffect(() => {
        setCurrentIdx(0);
    }, [total]);

    useEffect(() => {
        if (currentIdx > 0 && currentIdx >= total) {
            setCurrentIdx(total - 1);
        }
    }, [currentIdx, total]);

    const shipment = safeShipments[currentIdx];
    const backendTimeWindows = useMemo(
        () =>
            (shipment?.time_windows || [])
                .filter((tw: any) => tw?.start || tw?.end),
        [shipment]
    );

    const getTimeWindow = () => {
        const backendWindow = shipment?.time_windows?.[0];
        if (backendWindow?.start || backendWindow?.end) {
            return {
                source: 'backend',
                start: backendWindow.start,
                end: backendWindow.end,
            };
        }
        if (shipment?.type === 'pickup' && pickup_time_window) {
            return {
                source: 'settings',
                start: pickup_time_window.start,
                end: pickup_time_window.end,
            };
        }
        if (shipment?.type === 'delivery' && delivery_time_window) {
            return {
                source: 'settings',
                start: delivery_time_window.start,
                end: delivery_time_window.end,
            };
        }
        return null;
    };
    const timeWindow = getTimeWindow();
    const makeIcon = (type: 'pickup' | 'delivery') => {
        const svg = type === 'pickup' ? pickupMarkerMaker() : deliveryMarkerMaker();
        const url = encodeURI('data:image/svg+xml;utf8,' + svg).replace(/#/g, '%23');

        return L.icon({
            iconUrl: url,
            iconSize: [48, 48],
            iconAnchor: [24, 48],
            popupAnchor: [0, -24]
        });
    };

    function ChangeMapView({ coordinates }: ChangeMapViewProps): null {
        const map = useMap();
        useEffect(() => {
            if (!coordinates) return;
            map.setView([coordinates.latitude, coordinates.longitude] as LatLngExpression, 13);
        }, [map, coordinates]);
        return null;
    }

    function StopPropagation(): React.ReactElement | null {
        const map = useMap();
        React.useEffect(() => {
            const c = map.getContainer();
            L.DomEvent.disableClickPropagation(c);
            L.DomEvent.disableScrollPropagation(c);
        }, [map]);
        return null;
    }
    const stopUnlessMap = (e: React.SyntheticEvent<HTMLDivElement>) => {
        const tgt = e.nativeEvent.target as HTMLElement;
        if (tgt.closest('.leaflet-container')) return;
        e.stopPropagation();
    };

    const icon = useMemo(
        () => makeIcon(shipment?.type === 'pickup' ? 'pickup' : 'delivery'),
        [shipment?.type]
    );

    const formatTimeRange = (start: string | null, end: string | null) => {
        const toDate = (v: string | null) => {
            if (!v) return null;
            return /T|\+|\-00:/.test(v) ? new Date(v) : parseTimeStringToDate(v);
        };
        const sDate = toDate(start);
        const eDate = toDate(end);
        const s = sDate ? localTime(sDate) : '';
        const e = eDate ? localTime(eDate) : '';

        return (
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                <Typography component="span" variant="inherit">{s}</Typography>
                <Typography component="span" variant="inherit" sx={{ mx: 1 }}>--</Typography>
                <Typography component="span" variant="inherit">{e}</Typography>
            </Box>
        );
    };

    return (
        <Dialog open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                onMouseDownCapture: stopUnlessMap,
                onMouseMoveCapture: stopUnlessMap,
                onTouchMoveCapture: stopUnlessMap,
                onWheelCapture: stopUnlessMap,
            }}>
            <DialogTitle className={classes.Dialogtitle}>
                <Typography sx={{ fontWeight: 500, fontSize: 18 }}>
                    {i18next.t('common:routes.shipmenttitle')}{' '}
                </Typography>
                <IconButton onClick={onClose} className={classes.closeIconBtn}>
                    <CloseOutlined />
                </IconButton>
            </DialogTitle>

            <DialogContent className={classes.Dialogcontent}>
                {total === 0 ? (
                    <Typography>
                        {i18next.t('common:labels.noShipments')}
                    </Typography>
                ) : (
                    <>
                        {/* --- Stop Details --- */}
                        <Box className={classes.container}>
                            <Box className={classes.infoRow}>
                                <Box className={classes.infoColumn}>
                                    {/* Address */}
                                    <Box display="flex" alignItems="center" mt={2}>
                                        <LocationCityOutlined sx={{ mr: 1 }} />
                                        <Typography variant="subtitle2" className={classes.Textlabel}>
                                            {i18next.t('common:routes.Address')}
                                        </Typography>
                                    </Box>
                                    {stop?.location?.address?.street ? (
                                        <>
                                            <Typography className={classes.addresslabel}>
                                                {stop?.location?.address?.street}{' '}
                                                {stop?.location?.address?.house_number}
                                            </Typography>
                                            <Typography className={classes.addresslabel}>
                                                {stop?.location?.address?.postal_code}{' '}
                                                {stop?.location?.address?.city}
                                            </Typography>
                                            {stop?.location?.address?.country && (
                                                <Typography className={classes.addresslabel}>
                                                    {i18next.t(`countries:${stop.location.address.country}`, {
                                                        defaultValue: stop.location.address.country
                                                    })}
                                                </Typography>
                                            )}
                                        </>
                                    ) : (
                                        <Typography>--</Typography>
                                    )}

                                    {/* Stay on site */}
                                    <Box>
                                        <Box display="flex" alignItems="center" mt={2}>
                                            <TimelapseOutlined sx={{ mr: 1 }} />
                                            <Typography variant="subtitle2" className={classes.Textlabel}>
                                                {i18next.t('common:routes.onsiteStay')}
                                            </Typography>
                                        </Box>
                                        <Typography className={classes.labelText}>
                                            {readableDuration(stop.arrival_time, stop.departure_time) || '--'}
                                        </Typography>
                                    </Box>

                                    {/* ETA */}
                                    <Box>
                                        <Box display="flex" alignItems="center" mt={2}>
                                            <DepartureBoardOutlined sx={{ mr: 1 }} />
                                            <Typography variant="subtitle2" className={classes.Textlabel}>
                                                {i18next.t('common:routes.ETA')}
                                            </Typography>
                                        </Box>
                                        <Typography className={classes.labelText}>
                                            {localTime(new Date(stop.arrival_time)) || '--'}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Map */}
                                <Box className={classes.mapContainer}>
                                    {coordinates ? (
                                        <MapContainer
                                            center={[coordinates.latitude, coordinates.longitude]}
                                            zoom={13}
                                            style={{ width: '100%', height: '100%' }}
                                            scrollWheelZoom={false}
                                            zoomControl={false}
                                        >
                                            <StopPropagation />
                                            <TileLayer
                                                url={`https://maps.hereapi.com/v3/base/mc/{z}/{x}/{y}/png8?style=lite.day&apiKey=${API_KEY}&lang=de`}
                                                attribution={`Map © ${new Date().getFullYear()} HERE`}
                                                maxZoom={20}
                                            />
                                            <ZoomControl position="topright" />
                                            <ChangeMapView coordinates={stop.location.coordinates} />
                                            <Marker
                                                position={[
                                                    stop.location.coordinates.latitude,
                                                    stop.location.coordinates.longitude,
                                                ]}
                                                icon={icon}
                                            >
                                                <Popup className={classes.locationPop}>
                                                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                                                        {(() => {
                                                            const a = stop?.location?.address;
                                                            if (!a) return i18next.t('common:labels.notAvailable');
                                                            const line1 = [a.street, a.house_number].filter(Boolean).join(' ').trim();
                                                            const line2 = [a.postal_code, a.city].filter(Boolean).join(' ').trim();
                                                            const full = [line1, line2].filter(Boolean).join(', ');
                                                            return full || i18next.t('common:labels.notAvailable');
                                                        })()}
                                                    </Typography>
                                                </Popup>
                                            </Marker>
                                        </MapContainer>
                                    ) : (
                                        <Typography variant="body2" color="textSecondary">
                                            {i18next.t('common:labels.noCoordinates')}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        </Box>

                        {/* Auftrag Title */}
                        <Box>
                            <div className={classes.OrderSepration}>
                                <Typography sx={{ fontWeight: 500, fontSize: 18 }}>
                                    {i18next.t('common:routes.Odertitle')}{' '}
                                    {total > 0 ? `(${currentIdx + 1}/${total})` : ''}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <IconButton onClick={handlePrev} disabled={currentIdx === 0}>
                                        <ArrowBackIosOutlined />
                                    </IconButton>
                                    <IconButton
                                        onClick={handleNext}
                                        disabled={currentIdx === total - 1 || total === 0}
                                    >
                                        <ArrowForwardIosOutlined />
                                    </IconButton>
                                </Box>
                            </div>
                        </Box>

                        {/* Cutomer_ID & Priority */}
                        <Box
                            sx={{
                                p: 1.5,
                                boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                                mb: 1.5,
                            }}
                        >
                            <Grid container columnSpacing={3} alignItems="flex-start">
                                {/* Customer ID */}
                                <Grid item xs={12} md={4}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                        <AssignmentIndOutlined sx={{ mr: 1 }} />
                                        <Typography variant="subtitle2" className={classes.Textlabel}>
                                            {i18next.t('common:routes.customerId')}
                                        </Typography>
                                    </Box>
                                    <Typography className={classes.labelText}>
                                        {shipment.custom_id || '--'}
                                    </Typography>
                                </Grid>

                                {/* Firma */}
                                <Grid item xs={12} md={3}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                        <CorporateFareOutlined sx={{ mr: 1 }} />
                                        <Typography variant="subtitle2" className={classes.Textlabel}>
                                            {i18next.t('common:routes.firm')}
                                        </Typography>
                                    </Box>
                                    <Typography className={classes.labelText}>
                                        {shipment.customer_name || '--'}
                                    </Typography>
                                </Grid>

                                {/* Priority */}
                                <Grid item xs={12} md={4}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                        <LowPriorityOutlined sx={{ mr: 1 }} />
                                        <Typography variant="subtitle2" className={classes.Textlabel}>
                                            {i18next.t('common:routes.priority')}
                                        </Typography>
                                    </Box>
                                    <Typography className={classes.labelText}>
                                        {shipment.priority || '--'}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Box>

                        {/* Time Window */}
                        <Box sx={{ p: 1.5, boxShadow: '0 2px 5px rgba(0,0,0,0.1)', mb: 2 }}>
                            <Grid container columnSpacing={3} rowSpacing={1} alignItems="flex-start">
                                {/* Under Customer ID (col-1 = 4/12) */}
                                <Grid item xs={12} md={4}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                        <AccessTimeOutlined sx={{ mr: 1 }} />
                                        <Typography variant="subtitle2" className={classes.Textlabel}>
                                            {i18next.t('common:routes.timeWindow')}
                                        </Typography>
                                        <Tooltip
                                            title={
                                                backendTimeWindows.length > 0
                                                    ? i18next.t('common:routes.ShipmentInfo')
                                                    : timeWindow
                                                        ? i18next.t('common:routes.SettingsInfo')
                                                        : ''
                                            }
                                        >
                                            <InfoOutlined fontSize="small" sx={{ ml: 0.5, color: '#888', cursor: 'pointer' }} />
                                        </Tooltip>
                                    </Box>

                                    {backendTimeWindows.length > 0 ? (
                                        backendTimeWindows.map((tw: any, i: number) => (
                                            <Typography key={i}
                                                sx={{
                                                    fontWeight: 600,
                                                    color: '#000',
                                                    display: 'block',
                                                    fontSize: '15px',
                                                }}>
                                                {formatTimeRange(tw.start, tw.end)}
                                            </Typography>
                                        ))
                                    ) : (
                                        <Typography
                                            className={classes.labelText}
                                            sx={{ color: timeWindow ? '#888' : '#000', fontStyle: timeWindow ? 'italic' : 'normal' }}
                                        >
                                            {timeWindow ? formatTimeRange(timeWindow.start, timeWindow.end) : '--'}
                                        </Typography>
                                    )}
                                </Grid>

                                {/* Under Firma (col-2 = 3/12) */}
                                <Grid item xs={12} md={3}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                        <ConfirmationNumberOutlined sx={{ mr: 1 }} />
                                        <Typography variant="subtitle2" className={classes.Textlabel}>
                                            {i18next.t('common:routes.customerNum')}
                                        </Typography>
                                    </Box>
                                    <Typography className={classes.labelText}>
                                        {shipment.customer_number || '--'}
                                    </Typography>
                                </Grid>

                                {/* Spacer under Priority (col-3 = 4/12) */}
                                <Grid item xs={12} md={4} />
                            </Grid>
                        </Box>

                        {/* Load Info */}
                        {/* Load Info (boxed) */}
                        <Box sx={{ p: 1.5, boxShadow: '0 2px 5px rgba(0,0,0,0.1)', mb: 2 }}>
                            <Grid container columnSpacing={3} rowSpacing={1} alignItems="flex-start">
                                {dimensions.filter(Boolean).map((dim, i) => {
                                    const Icon = pickIconForLabel(dim.dimension);
                                    const idx = dim.backendIndex ?? i;
                                    const label = `${dim.dimension}${dim.unit ? ` [${dim.unit}]` : ''}`;
                                    const value =
                                        shipment?.loads && shipment.loads.length > idx
                                            ? shipment.loads[idx]
                                            : '--';

                                    return (
                                        <Grid item xs={12} md={4} key={i}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                                <Icon sx={{ mr: 1 }} />
                                                <Typography variant="subtitle2" className={classes.Textlabel}>
                                                    {label}
                                                </Typography>
                                            </Box>
                                            <Typography className={classes.labelText}>{value}</Typography>
                                        </Grid>
                                    );
                                })}
                                <ShipmentPoints points={shipment?.adr_points} />
                            </Grid>
                        </Box>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default DetailShipmentForm;
